import React, { useCallback, useEffect, useRef } from "react";
import { useState } from "react";
import { Box, Tooltip, Typography } from "@mui/material";
import PropTypes from "prop-types";
import { KeyboardArrowDown, KeyboardArrowRight } from "@mui/icons-material";
import { NoDeviceSiteIcon, SiteGrey } from "../../../assets/icons/index.js";
import { Tree } from "react-arborist";
import "../topology-fixed/TopologySidebar.scss";
import { useMemo } from "react";

/**
 * OverflowTooltip is a component that displays its children with ellipsis
 * when the content overflows its container. If the content overflows,
 * it shows a tooltip with the full content on hover.
 *
 * @param {React.ReactNode} props.children - The content to display within the tooltip.
 * @param {...any} props - Additional props to be passed to the Tooltip component.
 *
 * @return {JSX.Element} A Typography component wrapped with a Tooltip.
 */
function OverflowTooltip({ onClick, id, text, textProps, noDeviceTooltip = false,  disabledTooltip = false, ...props }) {
  const textRef = useRef(null);
  const [isOverflowing, setIsOverflowing] = useState(false);

  useEffect(() => {
    const el = textRef.current;
    if (el) {
      const hasOverflow = el.scrollWidth > el.clientWidth;
      setIsOverflowing(hasOverflow);
    }
  }, [text]);

  const showTooltip =  disabledTooltip || noDeviceTooltip || isOverflowing;
  const tooltipTitle =
    disabledTooltip
      ? "Parent site access not permitted"
      : noDeviceTooltip && isOverflowing
        ? `${text}\nThis site has no devices`
        : noDeviceTooltip
          ? "This site has no devices"
          : text;

  return (
    <Tooltip title={tooltipTitle} disableHoverListener={!showTooltip} {...props}>
      <Typography
        id={id}
        ref={textRef}
        variant="body1"
        noWrap
        onClick={onClick}
        sx={textProps}
      >
        {text}
      </Typography>
    </Tooltip>
  );
}

OverflowTooltip.propTypes = {
  children: PropTypes.object,
  id: PropTypes.string,
  text: PropTypes.string,
  onClick: PropTypes.func,
  textProps: PropTypes.object,
  noDeviceTooltip: PropTypes.bool,
  disabledTooltip: PropTypes.bool,
};

/**
 * TopologyLazyTree component renders a tree structure with lazy loading capabilities.
 *
 * @param {Object} props - The properties object.
 * @param {Array} props.treeData - The data for the tree structure.
 * @param {string} props.searchValue - The search term to filter the tree nodes.
 * @param {Function} [props.onNodeSelect=function() {}] - Callback function when a node is selected.
 * @param {Function} [props.onExpand=function() {}] - Callback function when a node is expanded.
 * @param {Array} props.selectedGrp - The selected groups.
 *
 * @return {JSX.Element} The rendered TopologyLazyTree component.
 */
export default function TopologyLazyTree({
  treeData,
  searchValue,
  onNodeSelect = function () { },
  onExpand = function () { },
  selectedGrp,
  isLoading = false,
  width = 222
}) {
  const [selectedNodeId, setSelectedNodeId] = useState(selectedGrp?.site_id);

  const containerRef = useRef(null);
  // const scrollPositionRef = useRef(0);
  const lastProcessedNodeRef = useRef(null);
  const pendingNodeRef = useRef(null);
  const [height, setHeight] = useState(0);


  useEffect(() => {
    if (containerRef.current) {
      setHeight(containerRef.current.clientHeight - 40);
    }
  }, [containerRef.current]);


  useEffect(() => {
    if (!isLoading && pendingNodeRef.current) {
      const pendingNode = pendingNodeRef.current;
      pendingNodeRef.current = null;

      if (
        !lastProcessedNodeRef.current ||
        pendingNode.data.site_id !== lastProcessedNodeRef.current.data.site_id
      ) {
        if (pendingNode.actionType === 'itemClick') {
          handleItemClick(pendingNode);
        } else if (pendingNode.actionType === 'rightIconClick') {
          handleRightIconClick(pendingNode);
        }
      }
    }
  }, [isLoading]);

  const handleItemClick = (node) => {
    if (isLoading) {
      pendingNodeRef.current = { ...node, actionType: 'itemClick' };
      return;
    }
    const activeItem = node.data;
    if (activeItem == null) return;
    onNodeSelect(activeItem);
    if (!activeItem.hasChildren) return;
    onExpand(activeItem.id);
    setSelectedNodeId(activeItem.id);
    lastProcessedNodeRef.current = node;
  };

  const handleRightIconClick = (node) => {
    if (isLoading) {
      pendingNodeRef.current = { ...node, actionType: 'rightIconClick' };
      return;
    }
    const activeItem = node.data;
    if (!activeItem) return;
    onNodeSelect({ showChildSites: true, siteId: activeItem?.site_id });
    lastProcessedNodeRef.current = node;
  };

  const treeRef = useRef();
  useEffect(() => {
    if (!selectedNodeId || !treeRef.current) return;
    const node = treeRef.current.get(selectedNodeId);
    if (node && !node.isOpen) {
      node.open();
    }
  }, [selectedNodeId]);

  // Functions to sort the data
  const naturalCompare = (a, b) => {
    const ax = [];
    const bx = [];

    a.site_name.replace(/(\d+)|(\D+)/g, function (_, $1, $2) {
      ax.push([$1 || Infinity, $2 || ""]);
    });

    b.site_name.replace(/(\d+)|(\D+)/g, function (_, $1, $2) {
      bx.push([$1 || Infinity, $2 || ""]);
    });

    while (ax.length && bx.length) {
      const an = ax.shift();
      const bn = bx.shift();
      const nn = (an[0] - bn[0]) || an[1].localeCompare(bn[1]);
      if (nn) return nn;
    }

    return ax.length - bx.length;
  };

  const sortTreeData = (data) => {
    if (!data || !Array.isArray(data)) return data;

    return data
      .map(item => ({
        ...item,
        children: sortTreeData(item.children)
      }))
      .sort(naturalCompare);
  };

  // useMemo to sort the treeData
  const sortedTreeData = useMemo(() => {
    return sortTreeData(treeData);
  }, [treeData]);

  /**
   * Node component represents a single node in a tree structure.
   *
   * @param {Object} props - The properties object.
   * @param {Object} props.node - The node object containing data and state.
   * @param {Object} props.node.data - The data associated with the node.
   * @param {boolean} props.node.isSelected - Indicates if the node is selected.
   * @param {boolean} props.node.isOpen - Indicates if the node is expanded.
   * @param {Function} props.node.toggle - Function to toggle the node's expanded state.
   * @param {Object} props.style - The style object to apply to the node.
   * @param {Function} props.dragHandle - The drag handle function for the node.
   *
   * @return {JSX.Element} The rendered node component.
   */
  const Node = useCallback(
    ({ node, style }) => {
      const nodeData = node.data;
      const isNoDeviceSite = nodeData?.ap_count === 0 && nodeData?.switch_count === 0;
      const isDisabledSite = nodeData?.accessible === 0;
      // if (!node.isOpen && nodeData.site_id == selectedNodeId) {
      //   node.toggle();
      // }
      return (
        <div
          style={{
            ...style,
            display: 'flex',
            alignItems: 'end',
            gap: '6px',
            width: `${width}px`,
            boxSizing: 'border-box',
            flexShrink: 0,
            backgroundColor: node.isSelected
              ? '#f5f1f7'
              : 'transparent',
            // pointerEvents: isLoading ? "none" : "auto",
          }}
        >
{
          nodeData?.hasChildren || nodeData.children?.length ? (
            <span
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                padding: '2px',
                cursor: isLoading ? 'not-allowed' : 'pointer',
              }}
              onClick={(e) => {
                e.stopPropagation();
                if (isLoading) return;

                if (!node.isOpen) {
                  handleRightIconClick(node);
                }

                node.toggle();

                if (node.isOpen && nodeData.site_id === selectedNodeId) {
                  setSelectedNodeId(null);
                }
              }}
            >
              {node.isOpen && nodeData.children?.length ? (
                <KeyboardArrowDown fontSize="16px" />
              ) : (
                <KeyboardArrowRight fontSize="16px" />
              )}
            </span>
          ) : (
            <Box width="16px" height="16px" />
          )
        }

          <Tooltip
            title={isNoDeviceSite ? 'This site has no devices' : ''}
            disableHoverListener={!isNoDeviceSite}
            placement="top"
            componentsProps={{
              tooltip: {
                sx: {
                  padding: '4px 8px',
                  boxShadow: '0 2px 4px 0 rgba(0, 0, 0, 0.16)',
                  border: 'solid 1px #dedede',
                  backgroundColor: '#fff',
                  color: '#000',
                  fontFamily: 'Inter-Regular',
                  fontSize: '12px',
                  fontWeight: 'medium',
                  fontWeight: 'normal',
                },
              },
            }}
            PopperProps={{
              modifiers: [
                {
                  name: 'preventOverflow',
                  options: {
                    boundary: 'clippingParents', // or a custom element
                    padding: 8,
                  },
                },
                {
                  name: 'offset',
                  options: {
                    offset: [100, -65],
                  },
                },
              ],
            }}
          >
            <div style={{ height: '24px', width: '24px' }}>
              {isNoDeviceSite ? <NoDeviceSiteIcon /> : <SiteGrey />}
            </div>
          </Tooltip>

          <OverflowTooltip
            text={nodeData.site_name}
            noDeviceTooltip={false} // Make sure this is false
            disabledTooltip={isDisabledSite}
            id={nodeData.site_id}
            placement="bottom-start"
            componentsProps={{
              tooltip: {
                sx: {
                  padding: '4px 8px',
                  boxShadow: '0 2px 4px 0 rgba(0, 0, 0, 0.16)',
                  border: 'solid 1px #dedede',
                  backgroundColor: '#fff',
                  color: '#000',
                  fontFamily: 'Inter-Regular',
                  whiteSpace: 'pre-line',
                  fontSize: '12px',
                  fontWeight: 'medium',
                  fontWeight: 'normal',
                  width: 'inherit',

                },
              },
            }}
            PopperProps={{
              modifiers: [
                {
                  name: 'preventOverflow',
                  options: {
                    boundary: 'clippingParents', // or a custom element
                    padding: 8,
                  },
                },
                {
                  name: 'offset',
                  options: {
                    offset: [-2, -7],
                  },
                },
              ],
            }}
            textProps={{
              width: `${width - 60}px`, // Fixed width calculation (total width - icons/padding)
              fontSize: "12px",
              fontFamily: "Inter-Regular",
              color: isDisabledSite ? "#B0B0B0" : "#545454",
              cursor: isDisabledSite ? "not-allowed" : "pointer",
              minWidth: "0",
              userSelect: "none", // Prevent text selection during drag
              opacity: isDisabledSite ? 0.8 : 1,
            }} 
            onClick={() => {
              if (isDisabledSite) return;
              handleItemClick(node);
            }}
          />
        </div>
      );
    },
    [selectedNodeId, isLoading, width]
  );

  Node.propTypes = {
    node: PropTypes.shape({
      data: PropTypes.object,
      isSelected: PropTypes.bool,
      isOpen: PropTypes.bool,
      toggle: PropTypes.func,
    }).isRequired,
    style: PropTypes.object.isRequired,
    dragHandle: PropTypes.func,
  };

  return (
    <div
      ref={containerRef}
      style={{
        flex: 1, display: "flex", minHeight: "80vh", maxHeight: "80vh", marginRight: '2px'
        //  pointerEvents: isLoading ? "none" : "auto",
      }}
    >
      {height > 0 && (
        <Tree
          ref={treeRef}
          dnd={false}
          className="sideBarContainer"
          data={sortedTreeData}
          openByDefault={false}
          // width={"222px"}
          height={height}
          width={`${width + 9}px`}
          searchTerm={searchValue}
          selection={selectedNodeId}
          idAccessor={(node) => node.site_id}
        >
          {Node}
        </Tree>
      )}
    </div>
  );
}

TopologyLazyTree.propTypes = {
  treeData: PropTypes.arrayOf(
    PropTypes.shape({
      site_id: PropTypes.string.isRequired,
      site_name: PropTypes.string.isRequired,
      children: PropTypes.arrayOf(
        PropTypes.shape({
          site_id: PropTypes.string.isRequired,
          site_name: PropTypes.string.isRequired,
          children: PropTypes.array,
        })
      ),
    })
  ).isRequired,
  searchValue: PropTypes.string.isRequired,
  onNodeSelect: PropTypes.func,
  onExpand: PropTypes.func,
  selectedGrp: PropTypes.object,
  isLoading: PropTypes.bool,
  width: PropTypes.number,
};
